import React, { useState } from 'react';
import { LeadFormData } from '../types';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { X, Calendar, ArrowUpRight } from 'lucide-react';
import { getActiveBadgeSession } from '../utils/badgeTracker';
import { FOUNDER_CONFIG } from '../config/conference';

interface LeadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const LeadModal: React.FC<LeadModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<LeadFormData>({
    fullName: '',
    email: '',
    company: '',
    jobRole: 'CTO / Chief Technology Officer',
    networkSize: '500 - 2,000 Devices',
    comments: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const activeBadge = getActiveBadgeSession();

    const payload = {
      ...formData,
      badgeTimestamp: activeBadge?.timestampMs || null,
      formattedBadgeTime: activeBadge?.formattedTime || null,
      conference: activeBadge?.conference || null,
      rawParam: activeBadge?.rawParam || null,
      createdAt: new Date().toISOString(),
      source: activeBadge?.isBadgeScan ? 'Badge QR Conference Review' : 'MT-HomePage Web Lead'
    };

    console.log('[LeadModal] Submitting lead payload:', payload);

    const saveToFirestore = addDoc(collection(db, 'demo_requests'), {
      ...formData,
      badgeTimestamp: activeBadge?.timestampMs || null,
      formattedBadgeTime: activeBadge?.formattedTime || null,
      conference: activeBadge?.conference || null,
      rawParam: activeBadge?.rawParam || null,
      createdAt: serverTimestamp(),
      source: activeBadge?.isBadgeScan ? 'Badge QR Conference Review' : 'MT-HomePage Web Lead'
    });

    const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 2000));

    try {
      // Race Firestore write with a 2s timeout so the user never gets stuck waiting
      await Promise.race([saveToFirestore, timeoutPromise]);
    } catch (err) {
      console.warn('[LeadModal] Firestore write warning/error:', err);
    }

    // Optional Webhook trigger if configured in env (e.g. Discord, Slack, Zapier, Resend API)
    const webhookUrl = (import.meta as any).env?.VITE_LEAD_WEBHOOK_URL;
    if (webhookUrl) {
      try {
        await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (webhookErr) {
        console.warn('[LeadModal] Webhook error:', webhookErr);
      }
    }

    setSubmitted(true);
    setLoading(false);
  };

  const handleReset = () => {
    setSubmitted(false);
    onClose();
  };

  return (
    <div className="minimal-modal-overlay" onClick={onClose}>
      <div className="minimal-modal-card" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>

        {!submitted ? (
          <div>
            <div className="modal-head">
              <div className="pill-tag mb-2">
                <span className="status-dot blue" />
                <span>OPERATIONAL REVIEW</span>
              </div>
              <h2>Schedule a 15-Minute Operational Review</h2>
              <p>
                Speak directly with the lead infrastructure architect: bring your hardware mix or last major outage to see what it looks like on one screen with transparent per-subscriber pricing.
              </p>
              <div className="quick-calendar-prompt">
                <span>Prefer to book a slot directly?</span>
                <a
                  href={FOUNDER_CONFIG.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="quick-cal-link"
                >
                  <Calendar size={13} />
                  <span>Open Calendar Scheduler</span>
                  <ArrowUpRight size={11} />
                </a>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-grid">
                <div className="field-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="Sarah Jenkins"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  />
                </div>

                <div className="field-group">
                  <label>Work Email *</label>
                  <input
                    type="email"
                    required
                    placeholder="sjenkins@regionalfiber.net"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div className="field-group">
                  <label>Organization / ISP Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="Horizon Broadband"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  />
                </div>

                <div className="field-group">
                  <label>Primary Role</label>
                  <select
                    value={formData.jobRole}
                    onChange={(e) => setFormData({ ...formData, jobRole: e.target.value })}
                  >
                    <option>ISP Owner / CEO</option>
                    <option>CTO / Chief Technology Officer</option>
                    <option>NOC Manager / Operations Director</option>
                    <option>Principal Network Architect</option>
                    <option>Network Infrastructure Engineer</option>
                  </select>
                </div>

                <div className="field-group full">
                  <label>Network Footprint Size</label>
                  <select
                    value={formData.networkSize}
                    onChange={(e) => setFormData({ ...formData, networkSize: e.target.value })}
                  >
                    <option>Under 100 Managed Devices</option>
                    <option>100 - 500 Devices</option>
                    <option>500 - 2,000 Devices</option>
                    <option>2,000 - 10,000 Devices</option>
                    <option>10,000+ Large Telecom Infrastructure</option>
                  </select>
                </div>

                <div className="field-group full">
                  <label>Hardware Mix / Primary Challenge (Optional)</label>
                  <textarea
                    rows={3}
                    placeholder="Tell us about your hardware mix or current alert volume issues..."
                    value={formData.comments}
                    onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                  />
                </div>
              </div>

              <button type="submit" className="btn modal-submit w-full" disabled={loading}>
                {loading ? 'Securing Request...' : 'Confirm 15-Minute Review'}
              </button>
            </form>
          </div>
        ) : (
          <div className="modal-success-view">
            <span className="status-dot green large-dot" />
            <h2>Review Confirmed</h2>
            <p className="success-msg">
              Thank you, <strong>{formData.fullName}</strong>. We have received your review request for <strong>{formData.company}</strong>.
            </p>
            <p className="sub-success">
              Our lead infrastructure architect will reach out to <strong>{formData.email}</strong> within 1 business day.
            </p>
            <button onClick={handleReset} className="btn modal-submit w-full mt-3">
              Back to Site
            </button>
          </div>
        )}
      </div>

      <style>{`
        .minimal-modal-overlay {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.85);
          backdrop-filter: blur(10px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
        }

        /* Dark Slate-Blue Modal Theme */
        .minimal-modal-card {
          background: #090d14;
          border: 1px solid #1e293b;
          border-radius: var(--radius-md);
          width: 100%;
          max-width: 580px;
          padding: 2.25rem;
          position: relative;
          box-sizing: border-box;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.85), 0 0 40px rgba(37, 99, 235, 0.1);
        }

        .minimal-modal-card .pill-tag {
          background: rgba(37, 99, 235, 0.12);
          border: 1px solid rgba(59, 130, 246, 0.3);
          color: #60a5fa;
        }

        .modal-close {
          position: absolute;
          top: 1.25rem; right: 1.25rem;
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          transition: color 0.15s ease;
        }

        .modal-close:hover { color: #ffffff; }

        .modal-head h2 {
          font-size: 1.6rem;
          margin-bottom: 0.4rem;
          color: #ffffff;
        }

        .modal-head p {
          font-size: 0.88rem;
          color: #94a3b8;
          margin-bottom: 0.85rem;
          line-height: 1.5;
        }

        .quick-calendar-prompt {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          font-size: 0.8rem;
          color: #cbd5e1;
          margin-bottom: 1.25rem;
          padding: 0.5rem 0.75rem;
          background: #05080e;
          border: 1px solid #1e293b;
          border-radius: var(--radius-sm);
        }

        .quick-cal-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          color: #60a5fa;
          font-weight: 600;
          text-decoration: none;
          transition: color 0.15s;
        }

        .quick-cal-link:hover {
          color: #93c5fd;
          text-decoration: underline;
        }

        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .field-group {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          min-width: 0;
        }

        .full { grid-column: span 2; }

        .field-group label {
          font-size: 0.78rem;
          font-weight: 500;
          color: #cbd5e1;
        }

        .field-group input,
        .field-group select,
        .field-group textarea {
          width: 100%;
          max-width: 100%;
          box-sizing: border-box;
          background: #05080e;
          border: 1px solid #1e293b;
          border-radius: var(--radius-sm);
          padding: 0.65rem 0.85rem;
          color: #ffffff;
          font-family: var(--font-body);
          font-size: 0.85rem;
          outline: none;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .field-group select option {
          background: #090d14;
          color: #ffffff;
        }

        .field-group input::placeholder,
        .field-group textarea::placeholder {
          color: #475569;
        }

        .field-group input:focus,
        .field-group select:focus,
        .field-group textarea:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        }

        .modal-submit {
          background: #2563eb;
          color: #ffffff;
          font-weight: 600;
          border: none;
          padding: 0.75rem 1.25rem;
          border-radius: var(--radius-sm);
          transition: all 0.2s ease;
        }

        .modal-submit:hover {
          background: #3b82f6;
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(37, 99, 235, 0.4);
        }

        .modal-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .modal-success-view {
          text-align: center;
          padding: 1.5rem 0;
        }

        .large-dot {
          width: 10px;
          height: 10px;
          margin-bottom: 1rem;
        }

        .modal-success-view h2 {
          font-size: 1.6rem;
          margin-bottom: 0.5rem;
          color: #ffffff;
        }

        .success-msg {
          font-size: 0.95rem;
          color: #e2e8f0;
          margin-bottom: 0.3rem;
        }

        .sub-success {
          font-size: 0.85rem;
          color: #94a3b8;
        }

        @media (max-width: 640px) {
          .minimal-modal-overlay {
            padding: 0.75rem;
          }
          .minimal-modal-card {
            padding: 1.25rem;
          }
          .form-grid {
            grid-template-columns: 1fr;
          }
          .full {
            grid-column: span 1;
          }
        }
      `}</style>
    </div>
  );
};
